/**
 * Survey component for handling survey options, star ratings, and form field updates
 * Vanilla JavaScript implementation
 */

// Constants for better maintainability
const SELECTORS = {
    SURVEY_OPTION: '.survey-option',
    SURVEY_OPTION_TITLE: '.survey-option__title',
    STARS_INITIAL: '.survey-stars .stars .initial i',
    STARS_HOVER: '.survey-stars .stars .hover i',
    STARS_SELECTED: '.survey-stars .stars .selected i',
    SURVEY_FORM: '.survey-section--form',
    SURVEY_REVIEW: '.survey-section--review',
    GFORM: 'form[id^="gform_"]'
};

const CLASSES = {
    ACTIVE: 'active'
};

const MESSAGES = {
    SELECT_OPTION_FIRST: 'Please select an option before rating.'
};

class Survey {
    constructor(blockElement) {
        this.block = blockElement;
        this.selectedRating = 0;
        this.selectedOption = null;
        this.formId = null;
        
        // Cache DOM elements for better performance
        this.surveyOptions = null;
        this.starsElements = null;
        this.formElements = {};

        this.init();
    }

    /**
     * Initialize the survey component
     */
    init() {
        // Cache form ID and elements early
        this.formId = this.getFormId();
        this.cacheFormElements();
        
        this.initializeSurveyOptions();
        this.initializeStarRating();
    }

    /**
     * Cache form input elements for performance
     */
    cacheFormElements() {
        if (!this.formId) return;

        const form = document.getElementById(`gform_${this.formId}`);
        if (!form) return;

        this.formElements.optionInput = form.querySelector('.selected-survey-option input');
        this.formElements.ratingInput = form.querySelector('.selected-survey-rate input');
    }

    /**
     * Initialize survey option selection with event delegation for better performance
     */
    initializeSurveyOptions() {
        this.surveyOptions = this.block.querySelectorAll(SELECTORS.SURVEY_OPTION);
        
        // Use event delegation on the parent for better performance with many options
        this.block.addEventListener('click', this.handleSurveyClick.bind(this));
    }

    /**
     * Handle clicks within the survey block (event delegation)
     */
    handleSurveyClick(event) {
        const target = event.target;
        const surveyOption = target.closest(SELECTORS.SURVEY_OPTION);
        
        if (surveyOption && this.block.contains(surveyOption)) {
            this.handleOptionClick(surveyOption);
        }
    }

    /**
     * Handle survey option click
     */
    handleOptionClick(option) {
        // Early return if same option is clicked
        if (this.selectedOption === option) return;

        // Remove active class from all options using cached elements
        if (this.surveyOptions) {
            this.surveyOptions.forEach((opt) => opt.classList.remove(CLASSES.ACTIVE));
        }
        
        // Add active class to clicked option
        option.classList.add(CLASSES.ACTIVE);
        this.selectedOption = option;

        // Update form field value using cached input
        this.updateOptionFormField(option);
    }

    /**
     * Update the option form field with selected value
     */
    updateOptionFormField(option) {
        if (!this.formElements.optionInput) return;

        const titleElement = option.querySelector(SELECTORS.SURVEY_OPTION_TITLE);
        const optionTitle = (titleElement && titleElement.textContent ? titleElement.textContent.trim() : '') || 
                           (option.textContent ? option.textContent.trim() : '') || 
                           option.dataset.value || '';

        if (optionTitle) {
            this.formElements.optionInput.value = optionTitle;
            // Trigger change event for any form validation or listeners
            this.triggerInputChange(this.formElements.optionInput);
        }
    }

    /**
     * Initialize star rating system
     */
    initializeStarRating() {
        const starsInit = this.block.querySelectorAll(SELECTORS.STARS_INITIAL);
        const starsHover = this.block.querySelectorAll(SELECTORS.STARS_HOVER);
        const starsSelected = this.block.querySelectorAll(SELECTORS.STARS_SELECTED);

        if (!starsInit.length) return;

        // Cache star elements
        this.starsElements = { 
            initial: starsInit, 
            hover: starsHover, 
            selected: starsSelected 
        };

        // Add event listeners to each star with bound methods for better performance
        starsInit.forEach((star, index) => {
            star.addEventListener("mouseenter", () => this.handleStarHover(index));
            star.addEventListener("mouseleave", this.handleStarLeave.bind(this));
            star.addEventListener("click", () => this.handleStarClick(index));
        });
    }

    /**
     * Trigger change event on input element
     */
    triggerInputChange(input) {
        const changeEvent = new Event('change', { 
            bubbles: true, 
            cancelable: true 
        });
        input.dispatchEvent(changeEvent);
    }

    /**
     * Handle star hover effect with optimized DOM manipulation
     */
    handleStarHover(starIndex) {
        if (!this.starsElements) return;
        
        this.updateStarVisibility(this.starsElements.hover, starIndex);
    }

    /**
     * Handle star mouse leave
     */
    handleStarLeave() {
        if (!this.starsElements) return;
        
        this.hideAllStars(this.starsElements.hover);
    }

    /**
     * Handle star click
     */
    handleStarClick(starIndex) {
        // Validate that an option is selected
        if (!this.selectedOption) {
            this.showAlert(MESSAGES.SELECT_OPTION_FIRST);
            return;
        }

        if (!this.starsElements) return;

        const newRating = starIndex + 1;
        
        // Early return if same rating is clicked
        if (this.selectedRating === newRating) return;

        // Update selected stars visibility
        this.updateStarVisibility(this.starsElements.selected, starIndex);

        // Store rating and update form
        this.selectedRating = newRating;
        this.updateRatingFormField();
        this.showSectionIfNeeded(this.selectedRating);
    }

    /**
     * Update star visibility efficiently
     */
    updateStarVisibility(stars, maxIndex) {
        stars.forEach((star, index) => {
            star.style.visibility = index <= maxIndex ? "visible" : "hidden";
        });
    }

    /**
     * Hide all stars in a collection
     */
    hideAllStars(stars) {
        stars.forEach((star) => {
            star.style.visibility = "hidden";
        });
    }

    /**
     * Update the rating form field with selected value
     */
    updateRatingFormField() {
        if (!this.formElements.ratingInput) return;

        this.formElements.ratingInput.value = this.selectedRating.toString();
        this.triggerInputChange(this.formElements.ratingInput);
    }

    /**
     * Show alert message (can be customized for better UX)
     */
    showAlert(message) {
        // TODO: Replace with custom modal/toast for better UX
        alert(message);
    }

    /**
     * Show appropriate section based on rating value with cached elements
     */
    showSectionIfNeeded(rating) {
        const form = this.block.querySelector(SELECTORS.SURVEY_FORM);
        const review = this.block.querySelector(SELECTORS.SURVEY_REVIEW);

        // Use more efficient approach with single reflow
        this.updateSectionVisibility(form, review, rating);
    }

    /**
     * Update section visibility efficiently to minimize reflows
     */
    updateSectionVisibility(form, review, rating) {
        const showForm = rating < 5;
        const showReview = rating === 5;

        // Batch DOM updates to minimize reflows
        if (form) {
            form.style.display = showForm ? "block" : "none";
        }
        
        if (review) {
            review.style.display = showReview ? "block" : "none";
        }

        // Scroll to visible element
        if (showForm) {
            this.scrollToElement(form);
        } else if (showReview) {
            this.scrollToElement(review);
        }
    }

    /**
     * Scroll to element with smooth behavior and error handling
     */
    scrollToElement(element) {
        if (!element) return;

        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
            try {
                element.scrollIntoView({ 
                    behavior: "smooth", 
                    block: "start", 
                    inline: "nearest" 
                });
            } catch (error) {
                // Fallback for older browsers
                element.scrollIntoView();
            }
        });
    }

    /**
     * Get the Gravity Form ID from the block with caching
     */
    getFormId() {
        const form = this.block.querySelector(SELECTORS.GFORM);
        if (!form || !form.id) return null;

        const match = form.id.match(/gform_(\d+)/);
        return match && match[1] ? match[1] : null;
    }

    /**
     * Get current survey state for debugging or external access
     */
    getSurveyState() {
        return {
            rating: this.selectedRating,
            option: this.selectedOption && this.selectedOption.textContent ? this.selectedOption.textContent.trim() : null,
            formId: this.formId
        };
    }

    /**
     * Reset survey to initial state
     */
    reset() {
        // Reset selections
        this.selectedRating = 0;
        this.selectedOption = null;

        // Clear form fields
        if (this.formElements.optionInput) {
            this.formElements.optionInput.value = '';
        }
        if (this.formElements.ratingInput) {
            this.formElements.ratingInput.value = '';
        }

        // Remove active classes
        if (this.surveyOptions) {
            this.surveyOptions.forEach((opt) => opt.classList.remove(CLASSES.ACTIVE));
        }

        // Hide star selections
        if (this.starsElements) {
            this.hideAllStars(this.starsElements.selected);
            this.hideAllStars(this.starsElements.hover);
        }

        // Hide sections
        const form = this.block.querySelector(SELECTORS.SURVEY_FORM);
        const review = this.block.querySelector(SELECTORS.SURVEY_REVIEW);
        
        if (form) form.style.display = "none";
        if (review) review.style.display = "none";
    }
}

// Auto-initialize surveys when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    const surveyBlocks = document.querySelectorAll('.theeblock--survey');
    
    surveyBlocks.forEach(block => {
        new Survey(block);
    });
});

// Export for manual initialization if needed
if (typeof module !== 'undefined' && module.exports) {
    module.exports = Survey;
} else if (typeof window !== 'undefined') {
    window.Survey = Survey;
}